import { ContentCopy, OpenInNew, Sync } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import TableDataGrid from "../../../components/TableDataGrid";
import { useAuth } from "../../../contexts/contexts";
import { getApiErrorMessage } from "../../../helpers/api/getApiErrorMessage";
import { formatCurrencyToShow } from "../../../helpers/currency";
import { defaultToast } from "../../../helpers/toast/defaultToast";
import { api } from "../../../services/api";
import { CustomerProposalFinancial, CustomerProposalPayment } from "./types";

interface Props {
    customerId: number;
}

type PaymentRow = CustomerProposalPayment & { label: string };

const PAID = 1;
const CANCELED = 3;

const paymentLink = (reference: string | null) =>
    reference
        ? `${window.env.REACT_APP_LINK_PAYMENT}checkout?id=${reference}`
        : "";

const formatDate = (value?: string | null) =>
    value ? dayjs(value).format("DD/MM/YYYY") : "-";

function PaymentStatusChip({ payment }: { payment: CustomerProposalPayment }) {
    if (payment.payment_status_id === PAID) {
        return (
            <Chip
                label="Pago"
                color="success"
                size="small"
                variant="outlined"
            />
        );
    }

    if (payment.payment_status_id === CANCELED) {
        return <Chip label="Cancelado" size="small" variant="outlined" />;
    }

    if (
        dayjs(payment.due_date).startOf("day").isBefore(dayjs().startOf("day"))
    ) {
        return (
            <Chip
                label="Vencido"
                color="error"
                size="small"
                variant="outlined"
            />
        );
    }

    return (
        <Chip
            label="Em aberto"
            color="warning"
            size="small"
            variant="outlined"
        />
    );
}

function SummaryCard({
    label,
    value,
    color,
}: {
    label: string;
    value: string;
    color?: string;
}) {
    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h6" sx={{ color, fontWeight: 700 }}>
                {value}
            </Typography>
        </Paper>
    );
}

export function CustomerProposalTab({ customerId }: Props) {
    const { hasPermissions } = useAuth();
    const [data, setData] = useState<CustomerProposalFinancial | null>(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    const getProposal = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api.get(`proposal/customer/${customerId}`);
            setData(response.data.response ?? null);
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(
                    error,
                    "Não foi possível carregar a proposta do cliente."
                )
            );
        } finally {
            setLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        getProposal();
    }, [getProposal]);

    const syncWithAsaas = async () => {
        if (!data) {
            return;
        }

        try {
            setSyncing(true);
            const response = await api.post(
                `proposal/sync-payments/${data.proposal.id}`
            );
            defaultToast(
                "success",
                response.data.message ?? "Proposta sincronizada com o Asaas."
            );
            await getProposal();
        } catch (error) {
            defaultToast(
                "error",
                getApiErrorMessage(
                    error,
                    "Não foi possível sincronizar a proposta com o Asaas."
                )
            );
        } finally {
            setSyncing(false);
        }
    };

    const copyLink = async (reference: string | null) => {
        try {
            await navigator.clipboard.writeText(paymentLink(reference));
            defaultToast("success", "Link de pagamento copiado.");
        } catch {
            defaultToast("error", "Não foi possível copiar o link.");
        }
    };

    const rows: PaymentRow[] = useMemo(() => {
        if (!data) {
            return [];
        }

        const total = data.installments.length;

        return [
            ...(data.entry ? [{ ...data.entry, label: "Entrada" }] : []),
            ...data.installments.map((installment, index) => ({
                ...installment,
                label: `Parcela ${index + 1}/${total}`,
            })),
        ];
    }, [data]);

    const columns: GridColDef<PaymentRow>[] = [
        {
            field: "label",
            headerName: "Pagamento",
            sortable: false,
            flex: 1,
            minWidth: 120,
        },
        {
            field: "amount",
            headerName: "Valor",
            sortable: false,
            flex: 1,
            minWidth: 110,
            valueFormatter: ({ value }) => `R$ ${formatCurrencyToShow(value)}`,
        },
        {
            field: "due_date",
            headerName: "Vencimento",
            sortable: false,
            flex: 1,
            minWidth: 110,
            valueFormatter: ({ value }) => formatDate(value),
        },
        {
            field: "payment_date",
            headerName: "Pago em",
            sortable: false,
            flex: 1,
            minWidth: 110,
            valueFormatter: ({ value }) => formatDate(value),
        },
        {
            field: "status",
            headerName: "Status",
            sortable: false,
            flex: 1,
            minWidth: 110,
            renderCell: ({ row }) => <PaymentStatusChip payment={row} />,
        },
        {
            field: "link",
            headerName: "Link",
            sortable: false,
            width: 100,
            align: "right",
            headerAlign: "right",
            renderCell: ({ row }) =>
                row.payment_reference && row.payment_status_id !== PAID ? (
                    <Box>
                        <IconButton
                            size="small"
                            title="Copiar link de pagamento"
                            onClick={() => copyLink(row.payment_reference)}
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            title="Abrir link de pagamento"
                            href={paymentLink(row.payment_reference)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <OpenInNew fontSize="small" />
                        </IconButton>
                    </Box>
                ) : null,
        },
    ];

    if (loading && !data) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (!data) {
        return (
            <Alert severity="info">
                Este cliente não possui proposta vinculada.
            </Alert>
        );
    }

    const { proposal, summary, entry } = data;
    const paymentType =
        entry?.form_payment?.type_form_payment?.description ??
        entry?.form_payment?.type_form_payment?.name;

    return (
        <Stack spacing={3}>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Stack spacing={0.5}>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                    >
                        <Typography variant="h6">
                            Proposta #{proposal.id}
                        </Typography>
                        {proposal.proposal_status?.name && (
                            <Chip
                                label={proposal.proposal_status.name}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {[
                            proposal.plan?.description &&
                                `Plano: ${proposal.plan.description}`,
                            paymentType && `Pagamento: ${paymentType}`,
                            proposal.user?.name &&
                                `Vendedor: ${proposal.user.name}`,
                            proposal.created_at &&
                                `Criada em ${formatDate(proposal.created_at)}`,
                        ]
                            .filter(Boolean)
                            .join(" · ")}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                        component={Link}
                        to={`/comercial/proposal/proposalDetails?id=${proposal.id}`}
                        target="_blank"
                        rel="noreferrer"
                        variant="outlined"
                        size="small"
                        endIcon={<OpenInNew />}
                    >
                        Abrir proposta
                    </Button>
                    {hasPermissions("pagamento.confirmar") && (
                        <LoadingButton
                            variant="contained"
                            size="small"
                            color="success"
                            startIcon={<Sync />}
                            loading={syncing}
                            onClick={syncWithAsaas}
                        >
                            Sincronizar com Asaas
                        </LoadingButton>
                    )}
                </Stack>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr 1fr",
                        md: "repeat(4, 1fr)",
                    },
                    gap: 2,
                }}
            >
                <SummaryCard
                    label="Total da proposta"
                    value={`R$ ${formatCurrencyToShow(summary.total)}`}
                />
                <SummaryCard
                    label="Pago"
                    value={`R$ ${formatCurrencyToShow(summary.paid)}`}
                    color="#2e7d32"
                />
                <SummaryCard
                    label="Em aberto"
                    value={`R$ ${formatCurrencyToShow(summary.open)}`}
                    color="#ed6c02"
                />
                <SummaryCard
                    label="Vencidos"
                    value={String(summary.overdue)}
                    color={summary.overdue > 0 ? "#d32f2f" : undefined}
                />
            </Box>

            <TableDataGrid
                rows={rows}
                columns={columns}
                loading={loading || syncing}
                autoHeight
                hideFooter
                hideColumnMenu
                hideExports
                hideFilter
                toolBar={false}
            />
        </Stack>
    );
}
